import { useCallback, useEffect, useMemo, useState } from 'react';

import { getPlatformSettings } from '../api';
import { getContrastColor } from './colorContrast';
import PlatformSettingsContext from './PlatformSettingsContext';

// Wasserblau, the leading color of the Mecklenburg-Vorpommern state brand.
const DEFAULT_COLOR = '#005E90';

// The tab title until an administrator names the platform; public/index.html
// carries the same, for the moment before the settings arrive.
export const DEFAULT_SITE_NAME = 'DiSI';

// Maps each admin-settable slot to its CSS custom property. Each one is only
// ever set when the admin picked an explicit color for that slot - otherwise
// the property stays unset and index.css's var(--x-override, fallback) chain
// keeps the MV defaults.
const SLOT_PROPERTIES = {
  headerColor: '--header-color-override',
  menuColor: '--menu-color-override',
  buttonColor: '--button-color-override',
  headingColor: '--heading-color-override',
  textColor: '--text-color-override',
};

function applyBrandColor(color) {
  const root = document.documentElement.style;

  root.setProperty('--brand-primary', color);
  root.setProperty(
    '--brand-primary-contrast',
    getContrastColor(color),
  );
}

// Header and menu carry text, so an admin-chosen background also needs a
// readable foreground; the header additionally gets a darker search well.
const DERIVED_PROPERTIES = {
  headerColor: {
    '--header-fg-override': (color) => getContrastColor(color),
    '--header-well-override': (color) =>
      `color-mix(in srgb, ${color} 85%, black 15%)`,
  },
  menuColor: {
    '--menu-fg-override': (color) => getContrastColor(color),
  },
};

function applySlotOverrides(settings) {
  const root = document.documentElement.style;

  for (const [key, property] of Object.entries(SLOT_PROPERTIES)) {
    const value = settings?.[key];

    if (value) {
      root.setProperty(property, value);
    } else {
      root.removeProperty(property);
    }

    for (const [derived, derive] of Object.entries(
      DERIVED_PROPERTIES[key] ?? {},
    )) {
      if (value) {
        root.setProperty(derived, derive(value));
      } else {
        root.removeProperty(derived);
      }
    }
  }
}

function PlatformSettingsProvider({ children }) {
  const [primaryColor, setPrimaryColor] = useState(DEFAULT_COLOR);
  const [headerColor, setHeaderColor] = useState(null);
  const [menuColor, setMenuColor] = useState(null);
  const [buttonColor, setButtonColor] = useState(null);
  const [headingColor, setHeadingColor] = useState(null);
  const [textColor, setTextColor] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);
  const [siteName, setSiteName] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const settings = await getPlatformSettings();

      const color = settings?.primaryColor || DEFAULT_COLOR;

      setPrimaryColor(color);
      setHeaderColor(settings?.headerColor ?? null);
      setMenuColor(settings?.menuColor ?? null);
      setButtonColor(settings?.buttonColor ?? null);
      setHeadingColor(settings?.headingColor ?? null);
      setTextColor(settings?.textColor ?? null);
      setLogoUrl(settings?.logoUrl ?? null);
      setSiteName(settings?.siteName ?? null);
      applyBrandColor(color);
      applySlotOverrides(settings);

      return settings;
    } catch (error) {
      // Branding is decorative - a failed fetch keeps the CSS defaults
      // already baked into index.css rather than breaking the page.
      console.error(
        'Could not load platform branding:',
        error,
      );

      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    document.title = siteName || DEFAULT_SITE_NAME;
  }, [siteName]);

  const value = useMemo(
    () => ({
      primaryColor,
      headerColor,
      menuColor,
      buttonColor,
      headingColor,
      textColor,
      logoUrl,
      siteName,
      isLoading,
      refresh,
    }),
    [
      primaryColor,
      headerColor,
      menuColor,
      buttonColor,
      headingColor,
      textColor,
      logoUrl,
      siteName,
      isLoading,
      refresh,
    ],
  );

  return (
    <PlatformSettingsContext.Provider value={value}>
      {children}
    </PlatformSettingsContext.Provider>
  );
}

export default PlatformSettingsProvider;
